<template>
  <div>
    <div class="voucher-inner-wrap">
      <nav class="ticket-tab">
        <ul>
          <li
            v-for="tab in tabList"
            :key="tab.key"
            :class="{ selected: voucherType === tab.key }"
          >
            <a
              href="javascript: void(0)"
              @click.prevent="moveToVoucherTab(tab.key)"
              >{{ tab.title }}</a
            >
          </li>
        </ul>
      </nav>

      <component
        :is="component"
        v-if="!isLoading"
        :voucher-purchase-fn="handlePurchase"
        :get-value-fn="getValue"
        :set-number-commas-fn="setNumberCommas"
        :voucher-list="voucherList"
        :body-description-list="bodyDescriptionList"
        :show-all-voucher-state="showAllVoucherState"
        :get-filtered-voucher-list="getFilteredVoucherList"
        @show-voucher="hideAllVoucherButton"
      />

      <template v-else>
        <div class="min-h-screen"></div>
        <LoadingBar />
      </template>
    </div>

    <VoucherNotice :voucher-type="voucherType" />

    <PopupChangeUpcomingPayment
      v-if="isShowConfirmPopup"
      :voucher-info="upcomingVoucherInfo"
      @click-submit="changePurchase"
      @click-close="isShowConfirmPopup = false"
    />
  </div>
</template>
<script setup>
  import { PURCHASE_ACTION_TYPE } from '@/common/normalize/normalize-check-purchase-action'
  import { getCheckPurchaseAction } from '@/common/requests/data-fetch/get-check-purchase-action'
  import { putUpcomingPayments } from '@/common/requests/data-fetch/put-upcoming-payments'
  import { PATH_NAME_SUBSCRIPTION_TICKET } from '@/common/resources/router.const'
  import { useVoucherList } from '@/common/use/useVoucherList'
  import braze from '@/common/utils/braze'
  import { getPurchaseProperties } from '@/common/utils/braze.util'
  import {
    setDisableScrolling,
    setNumberCommas,
    showRequiredLoginPopup,
    titleTagNameChange,
  } from '@/common/utils/common'
  import cookie from '@/common/utils/cookie'
  import {
    MSG_CHECK_PURCHASE_NETWORK_ERROR,
    MSG_COUPON_SERVER_ERROR,
  } from '@/common/utils/coupon/const'
  import { multiProfileAutoLogin } from '@/common/utils/login'
  import { handleVoucher } from '@/common/utils/modal/handle-voucher'
  import { getVoucherPurchase } from '@/common/utils/request'
  import LoadingBar from '@/components/ui/LoadingBar/LoadingBar.vue'
  import {
    VOUCHER_TICKET_LIST,
    VOUCHER_TYPE,
    VOUCHER_TYPE_MAP,
  } from '@/components/voucher/partner/const'
  import PopupChangeUpcomingPayment from '@/components/voucher/PopupChangeUpcomingPayment.vue'
  import PartnerVoucherList from '@/components/voucher/voucher-table/PartnerVoucherList'
  import TvingPartnerVoucherList from '@/components/voucher/voucher-table/TvingPartnerVoucherList'
  import WavveVoucherList from '@/components/voucher/voucher-table/WavveVoucherList'
  import VoucherNotice from '@/components/voucher/VoucherNotice.vue'
  import {
    computed,
    defineEmits,
    defineProps,
    onMounted,
    onUnmounted,
    ref,
    shallowRef,
    watch,
  } from 'vue'
  import { useRoute, useRouter } from 'vue-router/composables'

  const router = useRouter()
  const route = useRoute()

  const emit = defineEmits(['user-product-info'])

  const props = defineProps({
    fetchLegacyConversions: {
      type: Function,
      default: () => {},
    },
  })

  const voucherType = computed(
    () => route.query['voucher-type'] || VOUCHER_TYPE.TVING_PARTNER,
  )
  const component = shallowRef(null)
  const voucherList = ref([])
  const bodyDescriptionList = ref([])
  const dataList = ref([])
  const isShowConfirmPopup = ref(false)
  const upcomingVoucherInfo = ref({})
  const isLoading = ref(false)
  const voucherTypeMap = ref(VOUCHER_TYPE_MAP)
  const tabList = ref([])

  const { onExternalPurchase, onShowBillingPopup, closePopup, action } =
    handleVoucher()

  const {
    showAllVoucherButton,
    getFilteredVoucherList,
    hideAllVoucherButton,
    showAllVoucherState,
  } = useVoucherList()

  const brazeAndRefresh = async (params) => {
    if (params?.result === 'y') {
      braze.purchase(getPurchaseProperties(params))
    }
    await multiProfileAutoLogin()
    await setData()
  }

  const postMessage = async (e) => {
    if (e.origin === location.origin) {
      if (e.data?.eventMessage === 'purchaseComplete') {
        await brazeAndRefresh(e.data?.params)
      }
    }
  }

  const setComponent = (voucherType) => {
    switch (voucherType) {
      case VOUCHER_TYPE.WAVVE:
        component.value = WavveVoucherList
        break
      case VOUCHER_TYPE.PARTNER:
        component.value = PartnerVoucherList
        break
      case VOUCHER_TYPE.TVING_PARTNER:
        component.value = TvingPartnerVoucherList
        break
      default:
        component.value = WavveVoucherList
        break
    }
  }

  const moveToVoucherTab = (voucherType) => {
    const { name, query } = route
    router
      .push({
        name,
        query: { ...query, 'voucher-type': voucherType },
      })
      .catch(() => {})
  }

  const getValue = (voucher, key) => voucher?.concurrency?.[0]?.[key] || ''

  const checkPurchaseAction = async (voucherInfo) => {
    const { productid: productId } = voucherInfo

    isLoading.value = true
    const checkPurchaseResult = await getCheckPurchaseAction({ productId })
    isLoading.value = false

    if (!checkPurchaseResult || !checkPurchaseResult.data) {
      return {
        error: checkPurchaseResult.error,
      }
    }
    return checkPurchaseResult.data
  }

  const onClickProduct = async (voucherInfo) => {
    if (cookie.getCredential() === 'none') {
      return showRequiredLoginPopup()
    }
    const {
      actionType,
      requestProductInfo,
      requestNonePromotionProductInfo: nonePromotionProductInfo,
      promotionInfo,
      error,
    } = await checkPurchaseAction(voucherInfo)
    switch (actionType) {
      case PURCHASE_ACTION_TYPE.PAYLETTER_CALL: {
        voucherPurchase(voucherInfo)
        return
      }
      case PURCHASE_ACTION_TYPE.PUT_UPCOMING_PAYMENTS: {
        isShowConfirmPopup.value = true
        upcomingVoucherInfo.value = { voucherInfo, requestProductInfo }
        return
      }

      case PURCHASE_ACTION_TYPE.SHOW_100_PROMOTION_MODAL: {
        voucherInfo = { ...voucherInfo, nonePromotionProductInfo }
        await action.promotionModal(voucherInfo)
        return
      }

      case PURCHASE_ACTION_TYPE.SHOW_100_PROMOTION_CANCEL_NOTI_MODAL: {
        await action.promotionCancelNotificationModal(voucherInfo)
        return
      }

      case PURCHASE_ACTION_TYPE.UNSUBSCRIBE_DEFENSE: {
        const { isSuccess, payloads } = await action.voucherChangeModal(
          voucherInfo,
          promotionInfo?.title ?? '',
        )
        if (isSuccess) {
          if ((payloads?.buttonType ?? 'keep') === 'change') {
            voucherPurchase(voucherInfo)
          } else {
            closePopup()
          }
        }
        return
      }

      default: {
        const { data } = error?.data
        const errorMessage = data?.message || MSG_CHECK_PURCHASE_NETWORK_ERROR
        alert(errorMessage)
      }
    }
  }

  const voucherPurchase = (voucherInfo) => {
    const isExternal =
      voucherInfo?.concurrency?.[0]?.is_product_external?.toUpperCase() === 'Y'

    if (isExternal) {
      return onExternalPurchase(voucherInfo)
    }

    return onShowBillingPopup(voucherInfo)
  }

  const handlePurchase = (voucherInfo) => {
    voucherType.value === VOUCHER_TYPE.PARTNER
      ? voucherPurchase(voucherInfo)
      : onClickProduct(voucherInfo)
  }

  const changePurchase = async ({ productCode: productId }) => {
    const result = await putUpcomingPayments({ productId })
    isShowConfirmPopup.value = false
    if (result?.error) {
      alert(MSG_COUPON_SERVER_ERROR)
      return
    }
    router.push({ name: PATH_NAME_SUBSCRIPTION_TICKET }).catch(() => {})
  }

  const setTabList = () => {
    const tabs = dataList.value?.map((voucher) => voucher?.passgroup_type ?? '')

    tabList.value = VOUCHER_TICKET_LIST?.filter((tab) => tabs.includes(tab.key))
  }

  const fetchVoucherPurchase = async () => {
    try {
      const { data } = await getVoucherPurchase()
      return data
    } catch (error) {
      console.log(error)
    }
  }
  const setData = async () => {
    isLoading.value = true
    const { name } = route
    try {
      const { data } =
        name === 'voucher_index'
          ? await fetchVoucherPurchase()
          : await props.fetchLegacyConversions()

      dataList.value =
        typeof data?.json === 'string' ? JSON.parse(data.json) : data.json || {}

      setTabList()

      const voucherIndex = dataList.value.findIndex(
        (voucher) =>
          voucher.passgroup_type === voucherTypeMap.value[voucherType.value],
      )
      voucherList.value =
        dataList.value[voucherIndex]?.productgroups?.[0]?.products
      bodyDescriptionList.value =
        dataList.value[voucherIndex]?.description_list?.[0]?.pc ?? []
      showAllVoucherButton(voucherList.value, 'main-voucher')
      setComponent(voucherType.value)

      const userProductInfoData = data?.user_product_info ?? {}
      emit('user-product-info', userProductInfoData)
    } catch (error) {
      console.error(`Error Get Voucher Purchase: ${error.message}`)
    }
    isLoading.value = false
  }

  onMounted(() => {
    window.addEventListener('message', postMessage)
    setData()
    setDisableScrolling(false)
    titleTagNameChange('이용권 구매 : 웨이브 이용권')
  })

  onUnmounted(() => {
    window.removeEventListener('message', postMessage)
  })

  watch(
    () => route.query,
    () => {
      setData()
      closePopup()
    },
  )
</script>
<style lang="scss" src="@/assets/scss/category/ticket.scss"></style>
